import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const FlyToCollection = ({
  isActive,
  imageUrl,
  startPosition,
  imageDimensions,
  onComplete,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationVars, setAnimationVars] = useState({});
  const [isFlying, setIsFlying] = useState(false);

  useEffect(() => {
    if (!isActive || !imageUrl || !startPosition) return;

    // Find Collection tab element - look for NavLink with Collection text
    let collectionTab = document.querySelector('a[href="/collection"]');

    // If not found, try finding NavLink by text content "Collection"
    if (!collectionTab) {
      const allElements = document.querySelectorAll("*");
      collectionTab = Array.from(allElements).find(
        (element) =>
          element.textContent?.trim() === "Collection" &&
          (element.tagName === "A" || element.querySelector("a")),
      );

      // If we found a container, get the actual link inside it
      if (collectionTab && collectionTab.tagName !== "A") {
        const linkInside = collectionTab.querySelector("a");
        if (linkInside) {
          collectionTab = linkInside;
        }
      }
    }

    // Still not found? Try to find by partial href match
    if (!collectionTab) {
      const allLinks = document.querySelectorAll("a");
      collectionTab = Array.from(allLinks).find(
        (link) =>
          link.href?.includes("/collection") ||
          link.getAttribute("href") === "/collection",
      );
    }
    if (!collectionTab) {
      onComplete?.();
      return;
    }

    const collectionRect = collectionTab.getBoundingClientRect();
    const startRect = startPosition;

    // Use actual image dimensions or fallback to defaults
    const imgWidth = imageDimensions?.width || 150;
    const imgHeight = imageDimensions?.height || 150;

    // Calculate animation variables
    const startX = startRect.left + startRect.width / 2 - imgWidth / 2; // Center of start element minus half image width
    const startY = startRect.top + startRect.height / 2 - imgHeight / 2; // Center of start element minus half image height
    const endX = collectionRect.left + collectionRect.width / 2 - imgWidth / 2;
    // const endY = 104 + collectionRect.height / 2 - imgHeight / 2; // Fixed position at 104px from top
    const endY = collectionRect.top + collectionRect.height / 2 - imgHeight / 2; // Dynamic position from top

    setAnimationVars({
      "--start-x": `${startX}px`,
      "--start-y": `${startY}px`,
      "--end-x": `${endX}px`,
      "--end-y": `${endY}px`,
      "--img-width": `${imgWidth}px`,
      "--img-height": `${imgHeight}px`,
    });

    setIsVisible(true);

    // Start flying animation after a small delay to ensure element is rendered
    const flyTimer = setTimeout(() => {
      setIsFlying(true);
    }, 200);

    // Hide after animation completes
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      setIsFlying(false);
      onComplete?.();
    }, 1000); // Reduced from 1600ms to 1000ms for faster completion

    return () => {
      clearTimeout(flyTimer);
      clearTimeout(hideTimer);
    };
  }, [isActive, imageUrl, startPosition, imageDimensions, onComplete]);

  if (!isVisible || !imageUrl) return null;

  return createPortal(
    <FlyingImage
      src={imageUrl}
      alt="Flying to collection"
      style={animationVars}
      className={isFlying ? "flying" : ""}
    />,
    document.body,
  );
};

export default FlyToCollection;

const FlyingImage = styled.img`
  position: fixed;
  z-index: 99999;
  width: var(--img-width, 150px);
  height: var(--img-height, 150px);
  object-fit: cover;
  border-radius: 12px;
  pointer-events: none;
  object-fit: contain;
  top: 0;
  left: 0;

  /* Initial position from CSS variables */
  transform: translate(var(--start-x, 0px), var(--start-y, 0px)) scale(1);
  opacity: 1;

  /* Animation */
  transition:
    transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    scale 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &.flying {
    transform: translate(var(--end-x, 0px), var(--end-y, 0px)) scale(0.1);
    opacity: 0;
  }
`;
